/**
 * FindPwChangePage — A-2-4. 비밀번호 변경
 * navigate state: { loginId, resetToken } (A-2-3에서 전달)
 * POST /member/find-pw/change → 성공 시 /login (pwChanged 배너)
 * 실패 401: 기존 비밀번호 불일치 / 422: 직전 비밀번호와 동일
 */
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router';
import { Alert, Button } from '@indipia/ui-core';
import { AuthLayout } from '../../app/layouts/AuthLayout';
import { PasswordField } from '../components/PasswordField';
import { findPwChange } from '../api/member-auth';

function pwStrength(pw: string): 0 | 1 | 2 | 3 {
  if (!pw) return 0;
  const cats = [/[a-zA-Z]/.test(pw), /\d/.test(pw), /[!@#$%^&*()_+\-=[\]{}|;':",.<>?/]/.test(pw)].filter(Boolean).length;
  if (pw.length < 8 || cats < 2) return 1;
  if (cats === 2) return 2;
  return 3;
}
const PW_LABEL  = ['', '약함', '보통', '강함'];
const PW_COLORS = ['var(--shell-border)', 'var(--color-danger)', 'var(--color-warning)', 'var(--color-success)'];

export function FindPwChangePage() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const state     = (location.state ?? {}) as { loginId?: string; resetToken?: string };

  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error,     setError]     = useState<string | null>(null);

  useEffect(() => {
    if (!state.loginId || !state.resetToken) {
      navigate('/find-pw', { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!state.loginId || !state.resetToken) return null;

  const { loginId, resetToken } = state;

  const strength = pwStrength(newPw);
  const pwValid  = strength >= 2 && newPw.length >= 8
    && /[a-zA-Z]/.test(newPw) && /\d/.test(newPw)
    && /[!@#$%^&*()_+\-=[\]{}|;':",.<>?/]/.test(newPw);
  const canSubmit = pwValid && confirmPw.length > 0 && !isLoading;

  const mismatch = confirmPw.length > 0 && newPw.length > 0 && confirmPw !== newPw;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (newPw !== confirmPw) {
      setError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      await findPwChange({ loginId, resetToken, newPassword: newPw, confirmPassword: confirmPw });
      navigate('/login', { replace: true, state: { pwChanged: true } });
    } catch (e) {
      const err    = e as { response?: { status?: number; data?: { code?: string } } };
      const status = err?.response?.status;
      const code   = err?.response?.data?.code;
      if (status === 401 || code === 'PASSWORD_MISMATCH') {
        setError('비밀번호가 일치하지 않습니다.');
      } else if (status === 422) {
        setError('직전 비밀번호와 동일합니다. 다른 비밀번호를 사용해 주세요.');
      } else {
        setError('비밀번호 변경 중 오류가 발생했습니다. 다시 시도해 주세요.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      kicker="Reset Password"
      title="새 비밀번호 설정"
      subtitle="새로 사용할 비밀번호를 입력해 주세요."
      actions={
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          disabled={!canSubmit && !isLoading}
          loading={isLoading}
          onClick={() => void handleSubmit()}
        >
          변경 완료
        </Button>
      }
    >
      <div className="flex flex-col gap-md">
        <PasswordField
          label="새 비밀번호"
          size="lg"
          placeholder="8자 이상 · 영문 + 숫자 + 특수문자"
          value={newPw}
          onChange={(e) => { setNewPw(e.target.value); setError(null); }}
          error={newPw.length > 0 && !pwValid ? '최소 8자, 영문+숫자+특수문자를 포함해 주세요.' : undefined}
          hint="최소 8자, 영문+숫자+특수문자 포함"
          footer={newPw.length > 0 && (
            <div>
              <div className="flex gap-02">
                {[1, 2, 3].map((n) => (
                  <div
                    key={n}
                    className="h-px flex-1 transition-colors"
                    style={{
                      height: 3,
                      borderRadius: 'var(--radius-xs)',
                      background: strength >= n ? PW_COLORS[strength] : 'var(--shell-border)',
                    }}
                  />
                ))}
              </div>
              <p className="mt-02 text-caption" style={{ color: PW_COLORS[strength] }}>
                강도: {PW_LABEL[strength]}
              </p>
            </div>
          )}
        />

        <PasswordField
          label="새 비밀번호 확인"
          size="lg"
          placeholder="비밀번호를 다시 입력하세요"
          value={confirmPw}
          onChange={(e) => { setConfirmPw(e.target.value); setError(null); }}
          error={mismatch ? '비밀번호가 일치하지 않습니다.' : undefined}
        />

        {error && <Alert kind="error">{error}</Alert>}
      </div>
    </AuthLayout>
  );
}
